import { Fragment, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Transition } from "@headlessui/react";
import {
	CheckCircleIcon,
	XCircleIcon,
	ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { XMarkIcon } from "@heroicons/react/20/solid";

const TYPE_STYLES = {
	success: {
		iconBg: "bg-emerald-50",
		iconColor: "text-emerald-600",
		accent: "bg-emerald-500",
		Icon: CheckCircleIcon,
	},
	error: {
		iconBg: "bg-red-50",
		iconColor: "text-red-600",
		accent: "bg-red-500",
		Icon: XCircleIcon,
	},
	warning: {
		iconBg: "bg-amber-50",
		iconColor: "text-amber-600",
		accent: "bg-amber-500",
		Icon: ExclamationTriangleIcon,
	},
	loading: {
		iconBg: "bg-gray-100",
		iconColor: "text-gray-700",
		accent: "bg-gray-900",
		Icon: null,
	},
};

export default function Notification({
	showNotif,
	setShowNotif,
	contents = {
		type: "success",
		heading: "",
		message: "",
	},
	zindex = 50,
}) {
	/*
    Usage:
    Add tag at end of document.

    <Notification showNotif = { ...stateVariable to show notificaiton... }
                  setShowNotif = {...reference to corresponding setState function... }
                  contents = { ...object with configuration options as described below... }
    />

  */

	// contents prop object format
	/* contents = {
    type : string = "success" || "error" || "loading",
    heading : string = Title message of the notification,
    message : string = message content of the body of the notification,
  } */

	const notificationTime = 7;
	const [type, setType] = useState("success");

	useEffect(() => {
		if (contents?.type) {
			setType(contents.type);
		}
	}, [contents]);

	useEffect(() => {
		if (!showNotif) return;

		// Don't auto-dismiss loading notifications
		if (contents?.type === "loading") return;

		const timer = setTimeout(() => {
			setShowNotif(false);
		}, notificationTime * 1000);

		return () => clearTimeout(timer);
	}, [showNotif, contents?.type, setShowNotif]);

	const style = TYPE_STYLES[type] || TYPE_STYLES.success;
	const StatusIcon = style.Icon;

	// Use portal to render outside DOM hierarchy (fixes nesting warnings when used inside tables)
	const notificationContent = (
		<div
			aria-live="assertive"
			className="pointer-events-none fixed inset-x-0 top-0 flex justify-end p-4 sm:p-5"
			style={{ zIndex: zindex }}
		>
			<Transition
				show={Boolean(showNotif)}
				as={Fragment}
				enter="transform transition duration-300 ease-out"
				enterFrom="translate-x-3 opacity-0"
				enterTo="translate-x-0 opacity-100"
				leave="transform transition duration-200 ease-in"
				leaveFrom="translate-x-0 opacity-100"
				leaveTo="translate-x-3 opacity-0"
			>
				<div className="pointer-events-auto w-full max-w-sm overflow-hidden rounded-xl border border-gray-100/90 bg-white/95 shadow-xl backdrop-blur-md ring-1 ring-black/[0.03]">
					<div className={`h-0.5 w-full ${style.accent}`} aria-hidden="true" />
					<div className="p-4">
						<div className="flex items-start gap-3">
							<div
								className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${style.iconBg}`}
							>
								{type === "loading" ? (
									<svg
										className={`h-4 w-4 animate-spin ${style.iconColor}`}
										xmlns="http://www.w3.org/2000/svg"
										fill="none"
										viewBox="0 0 24 24"
										aria-hidden="true"
									>
										<circle
											className="opacity-25"
											cx="12"
											cy="12"
											r="10"
											stroke="currentColor"
											strokeWidth="4"
										/>
										<path
											className="opacity-75"
											fill="currentColor"
											d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
										/>
									</svg>
								) : StatusIcon ? (
									<StatusIcon className={`h-5 w-5 ${style.iconColor}`} aria-hidden="true" />
								) : null}
							</div>
							<div className="min-w-0 flex-1 pt-0.5">
								{contents?.heading ? (
									<p className="text-sm font-semibold tracking-tight text-gray-900">
										{contents.heading}
									</p>
								) : null}
								{contents?.message ? (
									<p
										className={`max-h-48 overflow-y-auto whitespace-pre-line break-words text-sm leading-relaxed text-gray-600 ${
											contents?.heading ? "mt-1" : ""
										}`}
									>
										{contents.message}
									</p>
								) : null}
							</div>
							<button
								type="button"
								className="inline-flex shrink-0 rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2"
								onClick={() => {
									setShowNotif(false);
								}}
							>
								<span className="sr-only">Close</span>
								<XMarkIcon className="h-5 w-5" aria-hidden="true" />
							</button>
						</div>
					</div>
				</div>
			</Transition>
		</div>
	);

	// Render into a dedicated portal container to avoid removeChild errors.
	// Third-party scripts (Clerk, Intercom, etc.) mutate document.body; rendering
	// directly into body can cause "node to be removed is not a child" when unmounting.
	const getPortalContainer = () => {
		if (typeof document === "undefined") return null;
		let container = document.getElementById("notification-portal-root");
		if (!container) {
			container = document.createElement("div");
			container.id = "notification-portal-root";
			document.body.appendChild(container);
		}
		return container;
	};

	const container = getPortalContainer();
	return container ? createPortal(notificationContent, container) : null;
}
